import { Badge, Button, Card, EmptyState, Spinner, Table, type TableColumn } from "../../ds";
import type { Firmware } from "../../lib/api";
import { fmtBytes, fmtDateTime } from "../../lib/format";

// Katalog der hochgeladenen Images. `null` = lädt noch.

/** Ein Image, das beim Upload nicht zum Modell passte, ist beim Einspielen ein
 *  Ziegelstein — das muss im Katalog sichtbar bleiben, nicht nur beim Upload. */
function Warnung({ firmware }: { firmware: Firmware }) {
  const bad = (firmware.checks ?? []).filter((c) => c.status === "mismatch");
  if (bad.length === 0) return null;
  return (
    <Badge variant="danger" dot title={bad.map((c) => `${c.label}: ${c.actual}`).join(" · ")}>
      passt nicht zum Modell
    </Badge>
  );
}

function columns(onDelete: (f: Firmware) => void): TableColumn<Firmware>[] {
  return [
    {
      key: "version",
      header: "Version",
      cell: (f) => (
        <span className="flex flex-wrap items-center gap-2">
          {f.openwrtVersion || f.version}
          {/* Die Bauart steht direkt neben der Version: zwei Images derselben
              Version sind sonst nur am sha256 zu unterscheiden. */}
          {f.tag && <Badge variant="info">{f.tag}</Badge>}
          {/* Ohne usign-Signatur flasht der Router das Image nicht. */}
          {!f.signed && (
            <Badge variant="danger" dot title="Keine usign-Signatur — nicht flashbar">
              unsigniert
            </Badge>
          )}
          <Warnung firmware={f} />
        </span>
      ),
    },
    {
      key: "build",
      header: "Build",
      mono: true,
      // Der Stempel aus dem Dateinamen benennt den Lauf. Ohne ihn sind zwei
      // Images derselben Version nur am sha256 zu unterscheiden.
      cell: (f) => f.build || "—",
    },
    { key: "model", header: "Modell", cell: (f) => f.model || "—" },
    {
      key: "board",
      header: "Gerät laut Image",
      mono: true,
      hideOnMobile: true,
      cell: (f) => f.imageBoard || "—",
    },
    {
      key: "revision",
      header: "Revision",
      mono: true,
      hideOnMobile: true,
      cell: (f) => f.revision || "—",
    },
    {
      key: "size",
      header: "Größe",
      num: true,
      hideOnMobile: true,
      cell: (f) => fmtBytes(f.sizeBytes),
    },
    {
      key: "sha",
      header: "sha256",
      mono: true,
      hideOnMobile: true,
      cell: (f) => `${f.sha256.slice(0, 12)}…`,
    },
    {
      key: "created",
      header: "Hochgeladen",
      hideOnMobile: true,
      cell: (f) => (
        <span title={f.uploadedBy ? `von ${f.uploadedBy}` : undefined}>
          {fmtDateTime(f.createdAt)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "actions",
      cell: (f) => (
        <div className="flex justify-end gap-2">
          {/* Mit Signatur kommt ein ZIP (Image + .sig) — ohne die .sig daneben
              lehnt sysupgrade das Image auf dem Testrouter ab. */}
          <a href={`/api/firmware/${f.id}/download`} download>
            <Button
              size="sm"
              variant="ghost"
              iconName="download"
              title={f.signed ? "ZIP mit Image und .sig" : "Image (.bin), keine Signatur vorhanden"}
            >
              {f.signed ? "Download (ZIP)" : "Download"}
            </Button>
          </a>
          <Button size="sm" variant="danger-outline" iconName="trash" onClick={() => onDelete(f)}>
            Löschen
          </Button>
        </div>
      ),
    },
  ];
}

export function FirmwareTable({
  firmware,
  onDelete,
}: {
  firmware: Firmware[] | null;
  onDelete: (f: Firmware) => void;
}) {
  return (
    <Card title="Firmware-Images" bodyPadding={false}>
      {firmware === null ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : firmware.length === 0 ? (
        <EmptyState
          iconName="box"
          title="Noch keine Images"
          description="Ein fertiges sysupgrade-Image aus dem Firmware-Repo hochladen."
        />
      ) : (
        <Table columns={columns(onDelete)} rows={firmware} rowKey={(f) => f.id} />
      )}
    </Card>
  );
}
